import React, { useState, useEffect } from 'react';
import { X, MessageCircle, User, Phone, MapPin } from 'lucide-react';
import { trackLead } from '../utils/trackLead';

/**
 * LongerRoadRequestModal
 *
 * Purpose-built enquiry form for the "Longer Road · Tirthan Valley" curated
 * trip. Different from the generic EnquireModal (no dates, no rooms, no
 * discount code) because this is a fixed-price 12-seat curated weekend that
 * needs qualitative screening.
 *
 * Captures:
 *   - Name, phone, city (mandatory)
 *   - Last road trip context
 *   - Vehicle they'll drive
 *   - What they're expecting
 *   - Anything else
 *
 * Sends the composed enquiry to +91 98711 55963 via wa.me deep-link.
 */
const WA_NUMBER = '919871155963';
const GH_BLUE = '#2C5F8D';
const AMBER = '#EBC773';
const pf = { fontFamily: "'Playfair Display', Georgia, serif" };

export default function LongerRoadRequestModal({ open, onClose }) {
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [city, setCity] = useState('');
  const [lastTrip, setLastTrip] = useState('');
  const [vehicle, setVehicle] = useState('');
  const [expecting, setExpecting] = useState('');
  const [anythingElse, setAnythingElse] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) { setError(''); setSubmitting(false); }
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, [open]);

  if (!open) return null;

  const validate = () => {
    if (!name.trim()) return 'Please tell us your name.';
    if (!phone.trim() || phone.replace(/\D/g, '').length < 10) return 'A valid phone number, please.';
    if (!city.trim()) return 'Which city are you driving out of?';
    if (!lastTrip.trim()) return 'Where was your last road trip? (This helps us know each other.)';
    if (!vehicle.trim()) return 'Which vehicle will you be driving up?';
    if (!expecting.trim()) return 'What are you hoping to take away from this weekend?';
    return '';
  };

  const handleSubmit = () => {
    const v = validate();
    if (v) { setError(v); return; }
    setError('');
    setSubmitting(true);

    const message = [
      `*Request to book · The Longer Road · Tirthan Valley*`,
      ``,
      `*Name:* ${name.trim()}`,
      `*Phone:* ${phone.trim()}`,
      `*City:* ${city.trim()}`,
      ``,
      `*Last road trip (where & when):*`,
      lastTrip.trim(),
      ``,
      `*Vehicle for this trip:*`,
      vehicle.trim(),
      ``,
      `*What I'm hoping for:*`,
      expecting.trim(),
      ...(anythingElse.trim() ? ['', `*Anything else:*`, anythingElse.trim()] : []),
      ``,
      `— sent from gohimalayan.com/itineraries/longer-road`,
    ].join('\n');

    // Best-effort analytics ping (fire and forget).
    try {
      trackLead({
        source: 'longer_road',
        context: 'request_to_book_modal',
        propertyName: 'The Longer Road · Tirthan Valley',
        payload: {
          name: name.trim(),
          phone: phone.trim(),
          city: city.trim(),
          last_trip: lastTrip.trim(),
          vehicle: vehicle.trim(),
          expecting: expecting.trim(),
          anything_else: anythingElse.trim() || null,
        },
      });
    } catch (_e) { /* analytics is best-effort */ }

    const waUrl = `https://wa.me/${WA_NUMBER}?text=${encodeURIComponent(message)}`;
    window.open(waUrl, '_blank', 'noopener');

    // Reset + close after a short beat so the wa.me tab has time to open.
    setTimeout(() => {
      setName(''); setPhone(''); setCity('');
      setLastTrip(''); setVehicle(''); setExpecting(''); setAnythingElse('');
      setSubmitting(false);
      onClose && onClose();
    }, 400);
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 sm:p-6 bg-stone-950/70 backdrop-blur-sm"
         onClick={onClose}
         data-testid="longer-road-request-modal">
      <div className="relative bg-[#FAFAF8] w-full max-w-[560px] max-h-[92vh] overflow-y-auto rounded-2xl shadow-2xl"
           onClick={(e) => e.stopPropagation()}>

        {/* Close */}
        <button onClick={onClose}
                className="absolute top-4 right-4 w-8 h-8 rounded-full bg-white/90 hover:bg-white flex items-center justify-center border border-stone-200 z-10"
                data-testid="request-modal-close"
                aria-label="Close">
          <X className="w-4 h-4 text-stone-600" />
        </button>

        {/* Header */}
        <div className="px-6 sm:px-8 pt-8 pb-6 border-b border-stone-200" style={{ backgroundColor: GH_BLUE }}>
          <p className="text-[10px] tracking-[0.4em] uppercase mb-2" style={{ color: AMBER }}>Request to book</p>
          <h3 className="text-2xl sm:text-[28px] leading-tight text-white" style={pf}>The Longer Road · Tirthan Valley</h3>
          <p className="text-[13px] text-stone-100/90 mt-2 leading-relaxed">
            Twelve couples, one weekend. Tell us a little about you &mdash; we&rsquo;ll be in touch within a day.
          </p>
        </div>

        {/* Form */}
        <div className="px-6 sm:px-8 py-6 space-y-5">

          {/* Contact block */}
          <div>
            <p className="text-[10px] tracking-[0.3em] uppercase text-stone-500 mb-3">You</p>
            <div className="grid sm:grid-cols-2 gap-3">
              <div className="relative">
                <User className="absolute left-3 top-3.5 w-4 h-4 text-stone-400" />
                <input type="text" value={name} onChange={(e) => setName(e.target.value)}
                       placeholder="Your name"
                       data-testid="request-name-input"
                       className="w-full pl-10 pr-3 py-3 rounded-lg border border-stone-300 focus:border-[#2C5F8D] focus:outline-none text-[14px] bg-white" />
              </div>
              <div className="relative">
                <Phone className="absolute left-3 top-3.5 w-4 h-4 text-stone-400" />
                <input type="tel" value={phone} onChange={(e) => setPhone(e.target.value)}
                       placeholder="Phone (with WhatsApp)"
                       data-testid="request-phone-input"
                       className="w-full pl-10 pr-3 py-3 rounded-lg border border-stone-300 focus:border-[#2C5F8D] focus:outline-none text-[14px] bg-white" />
              </div>
            </div>
            <div className="relative mt-3">
              <MapPin className="absolute left-3 top-3.5 w-4 h-4 text-stone-400" />
              <input type="text" value={city} onChange={(e) => setCity(e.target.value)}
                     placeholder="City you're driving out of"
                     data-testid="request-city-input"
                     className="w-full pl-10 pr-3 py-3 rounded-lg border border-stone-300 focus:border-[#2C5F8D] focus:outline-none text-[14px] bg-white" />
            </div>
          </div>

          {/* Screening questions */}
          <div>
            <p className="text-[10px] tracking-[0.3em] uppercase text-stone-500 mb-3">A few things about you</p>
            <div className="space-y-3">

              <div>
                <label className="text-[12px] text-stone-700 block mb-1.5">Where was your last road trip, and when?</label>
                <textarea value={lastTrip} onChange={(e) => setLastTrip(e.target.value)}
                          placeholder="e.g. Spiti · last September"
                          rows={2}
                          data-testid="request-last-trip-input"
                          className="w-full px-3 py-2.5 rounded-lg border border-stone-300 focus:border-[#2C5F8D] focus:outline-none text-[14px] bg-white resize-none" />
              </div>

              <div>
                <label className="text-[12px] text-stone-700 block mb-1.5">Which vehicle will you be driving for this adventure?</label>
                <input type="text" value={vehicle} onChange={(e) => setVehicle(e.target.value)}
                       placeholder="e.g. Wrangler / Fortuner / Thar / Endeavour"
                       data-testid="request-vehicle-input"
                       className="w-full px-3 py-2.5 rounded-lg border border-stone-300 focus:border-[#2C5F8D] focus:outline-none text-[14px] bg-white" />
              </div>

              <div>
                <label className="text-[12px] text-stone-700 block mb-1.5">What are you expecting from this adventure?</label>
                <textarea value={expecting} onChange={(e) => setExpecting(e.target.value)}
                          placeholder="e.g. quiet, the drive itself, meeting like-minded couples"
                          rows={2}
                          data-testid="request-expecting-input"
                          className="w-full px-3 py-2.5 rounded-lg border border-stone-300 focus:border-[#2C5F8D] focus:outline-none text-[14px] bg-white resize-none" />
              </div>

              <div>
                <label className="text-[12px] text-stone-700 block mb-1.5">Anything else you&rsquo;d like us to know? <span className="text-stone-400">(optional)</span></label>
                <textarea value={anythingElse} onChange={(e) => setAnythingElse(e.target.value)}
                          placeholder="Dietary preferences, driving companion's name, anything at all"
                          rows={2}
                          data-testid="request-anything-else-input"
                          className="w-full px-3 py-2.5 rounded-lg border border-stone-300 focus:border-[#2C5F8D] focus:outline-none text-[14px] bg-white resize-none" />
              </div>

            </div>
          </div>

          {error && (
            <p className="text-[13px] text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2" data-testid="request-error">{error}</p>
          )}

          {/* Submit */}
          <button onClick={handleSubmit}
                  disabled={submitting}
                  data-testid="request-submit-btn"
                  className="w-full inline-flex items-center justify-center gap-2 px-6 py-4 rounded-full font-semibold text-[14px] transition disabled:opacity-60"
                  style={{ backgroundColor: submitting ? '#8fa9c2' : GH_BLUE, color: 'white' }}>
            <MessageCircle className="w-4 h-4" />
            {submitting ? 'Opening WhatsApp…' : 'Send request on WhatsApp'}
          </button>

          <p className="text-[11px] text-stone-500 text-center leading-relaxed italic" style={pf}>
            Twelve seats. Closes 10 September. Your request lands on +91 98711 55963 and we&rsquo;ll get back within a day.
          </p>
        </div>
      </div>
    </div>
  );
}
